import { type BCDescriptionTitleData } from './schema';
import { type BlockEditProps } from '@plone/types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Editor, Node, Transforms, Range, createEditor } from 'slate';
import { ReactEditor, Editable, Slate, withReact } from 'slate-react';
// @ts-ignore-error
import { SidebarPortal, Icon } from '@plone/volto/components';
// @ts-ignore-error
import { BlockDataForm } from '@plone/volto/components/manage/Form';
import CTA from '@redturtle/qubicaamf-theme/components/Blocks/commons/CTA';
import { defineMessages, useIntl } from 'react-intl';
import blockIcon from '@redturtle/qubicaamf-theme/icons/pageintro.svg';
import '@redturtle/qubicaamf-theme/components/Blocks/BowlingCenterDescription/bcdescription.scss';
import {
  TextEditorWidget,
  useHandleDetachedBlockFocus,
} from '@redturtle/volto-slate-extras';
import config from '@plone/volto/registry';
import { P } from '@plone/volto-slate/constants';
import cx from 'classnames';
import RelatedProducts from './relatedproducts';

const messages = defineMessages({
  description: {
    id: 'Add a description…',
    defaultMessage: 'Add a description…',
  },
  text: {
    id: 'Text',
    defaultMessage: 'Text',
  },
});

function usePrevious(value) {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

type Props = BlockEditProps & {
  data: BCDescriptionTitleData;
};

export const BCDescriptionBlockEdit = (props: Props) => {
  const {
    block,
    blockNode,
    data,
    detached,
    editable,
    index,
    metadata,
    onAddBlock,
    onChangeField,
    onDeleteBlock,
    onFocusNextBlock,
    onFocusPreviousBlock,
    onSelectBlock,
    onChangeBlock,
    blocksConfig,
    blocksErrors,
    properties,
    selected,
  } = props;

  const intl = useIntl();
  const schema = blocksConfig[data['@type']].blockSchema({
    data,
    intl,
  });
  const [editor] = useState(withReact(createEditor()));
  const [initialValue] = useState([
    {
      type: P,
      children: [
        {
          text: metadata?.['description'] || properties?.['description'] || '',
        },
      ],
    },
  ]);
  const contentId = properties?.id || '';
  // @ts-expect-error
  const { bowling_center_products = [] } = properties;

  const prevSelected = usePrevious(selected);

  const text = useMemo(
    () => metadata?.['description'] || properties?.['description'] || '',
    [metadata, properties],
  );

  const placeholder = useMemo(
    () => data.placeholder || intl.formatMessage(messages['description']),
    [data.placeholder, intl],
  );
  const disableNewBlocks = useMemo(() => detached, [detached]);

  useEffect(() => {
    if (!prevSelected && selected) {
      if (editor.selection && Range.isCollapsed(editor.selection)) {
        // keep selection
        ReactEditor.focus(editor);
      } else {
        // nothing is selected, move focus to end
        ReactEditor.focus(editor);
        Transforms.select(editor, Editor.end(editor, []));
      }
    }
  }, [prevSelected, selected, editor]);

  useEffect(() => {
    // undo/redo handlerr
    const oldText = Node.string(editor);
    if (oldText !== text) {
      Transforms.insertText(editor, text, {
        at: [0, 0],
      });
    }
  }, [editor, text]);

  const handleChange = useCallback(() => {
    const newText = Node.string(editor);
    if (newText !== text) {
      onChangeField('description', newText);
    }
  }, [editor, onChangeField, text]);

  const handleKeyDown = useCallback(
    (ev) => {
      if (ev.key === 'Backspace' && Node.string(editor).length === 0) {
        ev.preventDefault();
        onDeleteBlock(block, true);
      } else if (ev.key === 'Return' || ev.key === 'Enter') {
        ev.preventDefault();
        if (!disableNewBlocks) {
          // @ts-expect-error
          onSelectBlock(
            onAddBlock(config.settings.defaultBlockType, index + 1),
          );
        }
      } else if (ev.key === 'ArrowUp') {
        ev.preventDefault();
        // @ts-expect-error
        onFocusPreviousBlock(block, blockNode.current);
      } else if (ev.key === 'ArrowDown') {
        ev.preventDefault();
        // @ts-expect-error
        onFocusNextBlock(block, blockNode.current);
      }
    },
    [
      index,
      blockNode,
      editor,
      onDeleteBlock,
      disableNewBlocks,
      onSelectBlock,
      onAddBlock,
      onFocusPreviousBlock,
      onFocusNextBlock,
      block,
    ],
  );

  const { selectedField, setSelectedField } = useHandleDetachedBlockFocus(
    props,
    'description',
  );

  const handleFocus = useCallback(() => {
    // @ts-expect-error
    onSelectBlock(block);
  }, [block, onSelectBlock]);

  const renderElement = useCallback(({ attributes, children }) => {
    return (
      <div {...attributes} className="documentDescription">
        {children}
      </div>
    );
  }, []);

  if (typeof window.__SERVER__ !== 'undefined') {
    return <div />;
  }

  return (
    <>
      <div className="description-container">
        <div className="description-wrapper left">
          <Slate
            editor={editor}
            onChange={handleChange}
            initialValue={initialValue}
            // @ts-expect-error
            className={cx('block description', {
              selected: selected,
            })}
          >
            <Editable
              readOnly={!editable}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              renderElement={renderElement}
              onFocus={handleFocus}
              aria-multiline="false"
            ></Editable>
          </Slate>
        </div>
        <div className="text-wrapper right">
          <div className="text">
            <TextEditorWidget
              {...props}
              fieldName="text"
              selected={selected && selectedField === 'text'}
              setSelected={setSelectedField}
              focusPrevField={() => {
                setSelectedField('description');
              }}
              placeholder={intl.formatMessage(messages.text)}
            />
          </div>
          {data.linkHref?.[0] && (
            <CTA
              href={data.linkHref[0]['@id']}
              onClick={(e: React.SyntheticEvent<HTMLLinkElement>) => {
                e.preventDefault();
              }}
              openLinkInNewTab={false}
              ctaButtonStyle={data.ctaButtonStyle}
              linkIcon={data.linkIcon}
              cta_color={data.cta_color}
            >
              {data.linkTitle}
            </CTA>
          )}
        </div>
        <div className="clear-inline-start"></div>
        {/* fix in case the related products are deleted from the website */}
        {bowling_center_products.length > 0 && !!bowling_center_products[0] && (
          <RelatedProducts items={bowling_center_products} isEditMode={false} />
        )}
        <div className="clear"></div>
      </div>

      {/* @ts-expect-error */}
      <SidebarPortal selected={selected}>
        {schema && (
          <BlockDataForm
            icon={<Icon size="24px" name={blockIcon} />}
            schema={schema}
            title={schema.title}
            onChangeField={(id: string, value: unknown) => {
              onChangeBlock(block, {
                ...data,
                [id]: value,
              });
            }}
            onChangeBlock={onChangeBlock}
            formData={data}
            block={block}
            errors={blocksErrors}
          />
        )}
      </SidebarPortal>
    </>
  );
};

export default BCDescriptionBlockEdit;
